import React, { useCallback, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMedia } from "@/contexts/MediaContext";
import VideoControls from "@/components/file/VideoControls";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildSteps } from "@/types/flows";
import { useFlow } from "@/contexts/FlowContext";
import { useFilePreview } from "@/contexts/FilePreviewContext";
import { useModel } from "@/contexts/ModelContext";
import { fileCarouselHintContainerId } from "@/components/file/useCarousel";
import { useFrameStable } from "@/contexts/FrameContext";
import { FileControls } from "@/components/file/Carousel/FileControls";
import { FileSwitcher } from "@/components/file/Carousel/FileSwitcher";
import { EmbeddedWorkflowActions } from "./EmbeddedWorkflowActions";
import { FileUploadModal } from "@/components/file/UploadModal/FileUploadModal";
import { FileInputArea } from "@/components/flow/build/Steps/Upload/VisualAreaContent/FileInputArea";
import { useFlowState } from "@/hooks/useFlowState";
import { useCurrentMedia, useCurrentWorkflow } from "@/store";
import { LoadingOverlay } from "@/components/file/Preview/Loading";
import { useDownloadAnnotatedFile } from "@/hooks/useDownloadAnnotatedFile";
import { useShareWorkflow } from "@/hooks/useShareWorkflow";
import { useWorkflowObjectName } from "@/hooks/useWorkflowObjectName";
import { Chip } from "@scripts/react/components/Chip";
import {
    useFileCarouselNavigation,
    useFileCarouselThumbnails,
    useFileCarouselKeyboardNavigation,
    useFileCarouselAutoScroll
} from "@/hooks/useFileCarousel";
import { FloatingNavigationControls } from "@/components/shared/FloatingNavigationControls";
import { UploadLimitErrorType } from "@/constants/media";

// Container ID constants to avoid hardcoded strings
const EMBEDDED_THUMBNAIL_CONTAINER_ID = "embedded-thumbnail-container";

interface EmbeddedWorkflowPreviewProps {
    children: React.ReactNode;
    className?: string;
    onAddMediaItems: (files: File[], onFinally?: () => void) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
}

export const EmbeddedWorkflowPreview: React.FC<EmbeddedWorkflowPreviewProps> = ({
    children,
    className,
    onAddMediaItems,
    uploadLimitError: uploadLimitErrorProp,
    clearUploadLimitError: clearUploadLimitErrorProp
}) => {
    const { flowLocked } = useFlow();
    const { step } = useFlowState();
    const { hasMultipleFiles, videoControls } = useFilePreview();
    const {
        deleteMediaItem,
        erroredMediaItems,
        orderedMediaItems,
        uploadLimitError: uploadLimitErrorFromContext,
        clearUploadLimitError: clearUploadLimitErrorFromContext
    } = useMedia();

    // Use props if provided (e.g., from share flow), otherwise fall back to context
    const uploadLimitError = uploadLimitErrorProp ?? uploadLimitErrorFromContext;
    const clearUploadLimitError = clearUploadLimitErrorProp ?? clearUploadLimitErrorFromContext;
    const { isTestingNewFiles, isDownloading } = useModel();
    const workflow = useCurrentWorkflow();
    const currentMedia = useCurrentMedia();
    // Use shared download hook
    const handleDownloadAnnotatedFileClick = useDownloadAnnotatedFile({
        watermarkUrl: "https://rapid.roboflow.com"
    });

    // Use shared share workflow hook
    const { shareUrl, copied, handleShare: handleShareClick } = useShareWorkflow();

    // Extract object names from workflow name using shared hook
    const { objectNames } = useWorkflowObjectName();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isReviewStep = useMemo(() => step === BuildSteps.REVIEW, [step]);
    const { orderedTimelineFramePreviewUrls } = useFrameStable();

    // Use shared hooks
    const thumbnails = useFileCarouselThumbnails(
        orderedMediaItems,
        orderedTimelineFramePreviewUrls
    );
    const { handlePrevFile, handleNextFile } = useFileCarouselNavigation({
        mediaItems: orderedMediaItems,
        fileId: currentMedia?.file.id,
        flowLocked,
        hasMultipleFiles
    });

    useFileCarouselAutoScroll({
        fileId: currentMedia?.file.id,
        hasMultipleFiles,
        containerId: EMBEDDED_THUMBNAIL_CONTAINER_ID
    });
    useFileCarouselKeyboardNavigation(hasMultipleFiles, flowLocked, handlePrevFile, handleNextFile);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const addMediaItems = useCallback(
        async (files: File[]) => {
            if (flowLocked) {
                console.warn("🎠 EmbeddedWorkflowPreview: Flow is locked, returning");
                return;
            }

            return await onAddMediaItems(files, closeModal);
        },
        [onAddMediaItems, closeModal, flowLocked]
    );

    return (
        <div
            className={twClassNames(
                "flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg",
                className
            )}
        >
            {/* Header with responsive layout */}
            <div className="flex flex-col border-b border-gray-300 px-4 py-3 text-base font-medium md:flex-row md:items-center md:justify-between">
                {/* Header content - shown on all screen sizes */}
                <div className="flex items-center gap-2">
                    <img src="/images/logo.svg" alt="Logo" className="h-auto w-5" />
                    <span className="text-tertiary">Try Out My Model</span>
                    <span className="text-primary">{workflow?.name}</span>
                    {objectNames?.map((obj, index) => (
                        <Chip key={index} color="gray" type="text" size="small">
                            {obj}
                        </Chip>
                    ))}
                </div>

                {/* Actions - shown only on md+ screens */}
                <div className="hidden md:block">
                    <EmbeddedWorkflowActions
                        onDownloadClick={handleDownloadAnnotatedFileClick}
                        onShareClick={handleShareClick}
                        isDownloading={isDownloading}
                        shareUrl={shareUrl}
                        copied={copied}
                    />
                </div>
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key="with-files"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className={twClassNames("flex h-full w-full flex-col md:flex-row")}
                >
                    {/* File Preview Area */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
                        className={twClassNames(
                            "fileCarouselWindow flex h-full flex-col !rounded-none !border-none border-gray-300 bg-black !shadow-none md:w-3/4",
                            !videoControls && "overflow-hidden"
                        )}
                    >
                        <FileControls
                            filename={currentMedia?.file.file?.name}
                            flowLocked={flowLocked}
                        />

                        <div
                            className="relative flex flex-1 flex-col"
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            {/* FileSwitcher - Show on hover when multiple files */}
                            <AnimatePresence>
                                {hasMultipleFiles && isHovered && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        className="pointer-events-none absolute inset-0 z-10"
                                    >
                                        <FileSwitcher
                                            orderedMediaItems={orderedMediaItems}
                                            hasMultipleFiles={hasMultipleFiles}
                                            thumbnails={thumbnails}
                                            deleteMediaItem={deleteMediaItem}
                                            isTestingNewFiles={isTestingNewFiles}
                                            isReviewStep={isReviewStep}
                                            setIsModalOpen={setIsModalOpen}
                                            onAddMediaItems={addMediaItems}
                                            isDownloading={isDownloading}
                                            isShareMode={true}
                                            containerId={EMBEDDED_THUMBNAIL_CONTAINER_ID}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* portal for children to render hints */}
                            <div id={fileCarouselHintContainerId} className="floatingHint" />
                            {/* main content */}
                            <div className="flex flex-1 flex-col">
                                {hasMultipleFiles && isHovered && (
                                    <FloatingNavigationControls
                                        onPrevious={handlePrevFile}
                                        onNext={handleNextFile}
                                        buttonType="dark-primary"
                                        disabled={flowLocked}
                                    />
                                )}
                                {children}
                            </div>
                            {/* Loading overlay for download progress */}
                            <LoadingOverlay
                                show={isDownloading}
                                loadingText={
                                    currentMedia?.file.isVideo
                                        ? "Processing video..."
                                        : "Preparing download..."
                                }
                            />
                        </div>
                        {/* Video controls - rendered outside the main content */}
                        {currentMedia?.file.isVideo && (
                            <VideoControls controls={videoControls} flowLocked={flowLocked} />
                        )}
                    </motion.div>

                    {/* Upload Zone */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
                        className="flex min-w-64 gap-3 border-l border-gray-300 p-6 md:w-1/4 md:flex-col"
                    >
                        <FileInputArea
                            addMediaItems={addMediaItems}
                            erroredMediaItems={erroredMediaItems}
                            showAllMethods={false}
                            isSharePage={true}
                            qrSize={80}
                            uploadLimitError={uploadLimitError}
                            clearUploadLimitError={clearUploadLimitError}
                        />
                    </motion.div>
                </motion.div>
            </AnimatePresence>

            {isModalOpen && (
                <FileUploadModal
                    title={isReviewStep ? "Test on More Files" : "Add Files"}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onAddMediaItems={addMediaItems}
                    erroredMediaItems={erroredMediaItems}
                />
            )}
        </div>
    );
};
